import { useEffect, useRef, useState, type FormEvent } from "react";
import {
  Cards,
  CaretLeft,
  CaretRight,
  ClipboardText,
  Lock,
  LockOpen,
  Plus,
  Trash,
  X,
} from "@phosphor-icons/react";
import {
  FLASHCARD_AI_PROMPT,
  LIMITS,
  parseFlashcardText,
  type FlashCard,
} from "@yay/shared";

interface FlashcardStageProps {
  cards: FlashCard[];
  cardIndex: number;
  flipped: boolean;
  disabled?: boolean;
  /** Full-bleed layout (video hidden): side arrows + corner tools. */
  fullBleed?: boolean;
  /** When set, flip/nav are locked to the leader's view. */
  followingName?: string | null;
  onUnfollow?: () => void;
  onFlip: () => void;
  onNav: (delta: number) => void;
  onAdd: (term: string, definition: string) => void;
  onImport: (cards: Array<{ term: string; definition: string }>) => void;
  onUpdate: (id: string, term: string, definition: string) => void;
  onDelete: (id: string) => void;
  /** Whether the shared deck is locked for editing. */
  editLocked?: boolean;
  onToggleLock?: (locked: boolean) => void;
  /** Keep the card face out of tab order (e.g. while the chat pill is focused). */
  unfocusable?: boolean;
}

export function FlashcardStage({
  cards,
  cardIndex,
  flipped,
  disabled,
  fullBleed = false,
  followingName,
  onUnfollow,
  onFlip,
  onNav,
  onAdd,
  onImport,
  onUpdate,
  onDelete,
  editLocked = false,
  onToggleLock,
  unfocusable = false,
}: FlashcardStageProps) {
  const card = cards[cardIndex] ?? null;
  const [editing, setEditing] = useState(false);
  const [adding, setAdding] = useState(false);
  const [batchOpen, setBatchOpen] = useState(false);
  const [term, setTerm] = useState("");
  const [definition, setDefinition] = useState("");
  const [batchText, setBatchText] = useState("");
  const [promptCopied, setPromptCopied] = useState(false);
  // `window.setTimeout` returns number — but `ReturnType<typeof window.setTimeout>`
  // resolves to Node's Timeout here (window is Window & typeof globalThis with
  // @types/node loaded), so type the handle explicitly as number.
  const copyToastTimerRef = useRef<number | null>(null);
  const [importError, setImportError] = useState("");
  const faceTextRef = useRef<HTMLSpanElement>(null);
  const prevFaceRef = useRef<string | null>(null);
  const revealTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setEditing(false);
    setAdding(false);
  }, [cardIndex, card?.id]);

  const faceText = card
    ? flipped
      ? card.definition
      : card.term
    : "";

  // Soft in-place reveal — no remount, no blur (those caused flicker).
  useEffect(() => {
    const el = faceTextRef.current;
    if (!el) return;

    // First paint: seed baseline, don't animate.
    if (prevFaceRef.current === null) {
      prevFaceRef.current = faceText;
      return;
    }
    if (prevFaceRef.current === faceText) return;
    prevFaceRef.current = faceText;

    // Followers get frequent sync updates — keep them snap-clean.
    if (followingName) return;

    if (revealTimerRef.current) clearTimeout(revealTimerRef.current);
    el.classList.remove("reveal");
    // Restart CSS animation on the same node.
    void el.offsetWidth;
    el.classList.add("reveal");
    revealTimerRef.current = setTimeout(() => {
      el.classList.remove("reveal");
      revealTimerRef.current = null;
    }, 400);

    return () => {
      if (revealTimerRef.current) {
        clearTimeout(revealTimerRef.current);
        revealTimerRef.current = null;
      }
    };
  }, [faceText, followingName]);

  function startEdit() {
    if (!card || disabled || editLocked) return;
    setTerm(card.term);
    setDefinition(card.definition);
    setAdding(false);
    setEditing(true);
  }

  function startAdd() {
    if (disabled || editLocked) return;
    setTerm("");
    setDefinition("");
    setEditing(false);
    setAdding(true);
  }

  function openBatch() {
    if (disabled || editLocked) return;
    setBatchText("");
    setImportError("");
    setBatchOpen(true);
  }

  function handleSave(e: FormEvent) {
    e.preventDefault();
    const t = term.trim();
    const d = definition.trim();
    if (!t || !d) return;
    if (adding) {
      onAdd(t, d);
      setAdding(false);
    } else if (card) {
      onUpdate(card.id, t, d);
      setEditing(false);
    }
  }

  function handleImport(e: FormEvent) {
    e.preventDefault();
    const parsed = parseFlashcardText(batchText);
    if (parsed.length === 0) {
      setImportError("No cards found. Use: Term :: Definition");
      return;
    }
    onImport(parsed.slice(0, LIMITS.MAX_FLASHCARDS));
    setBatchOpen(false);
    setBatchText("");
    setImportError("");
  }

  useEffect(() => {
    return () => {
      if (copyToastTimerRef.current) {
        clearTimeout(copyToastTimerRef.current);
      }
    };
  }, []);

  async function copyAiPrompt() {
    try {
      await navigator.clipboard.writeText(FLASHCARD_AI_PROMPT);
      if (copyToastTimerRef.current) {
        clearTimeout(copyToastTimerRef.current);
      }
      setPromptCopied(false);
      // Restart animation if clicked again while visible.
      requestAnimationFrame(() => {
        setPromptCopied(true);
        copyToastTimerRef.current = window.setTimeout(() => {
          setPromptCopied(false);
          copyToastTimerRef.current = null;
        }, 1800);
      });
    } catch {
      /* clipboard blocked */
    }
  }

  const countLabel =
    cards.length === 0 ? "0/0" : `${cardIndex + 1}/${cards.length}`;

  const busy = editing || adding || batchOpen;
  const viewLocked = !!followingName;
  const empty = cards.length === 0;
  const showGuide = empty && !busy && !viewLocked;

  const canSave = term.trim().length > 0 && definition.trim().length > 0;

  const editForm = (editing || adding) && (
    <form className="nerd-flash-form" onSubmit={handleSave}>
      <input
        className="nerd-flash-input-term"
        value={term}
        onChange={(e) => setTerm(e.target.value)}
        placeholder="Term"
        maxLength={LIMITS.MAX_FLASH_TERM}
        required
        autoFocus
      />
      <textarea
        className="nerd-flash-input-def"
        value={definition}
        onChange={(e) => setDefinition(e.target.value)}
        placeholder="Definition"
        maxLength={LIMITS.MAX_FLASH_DEFINITION}
        rows={3}
        required
      />
      <div className="nerd-flash-form-row">
        <button
          type="submit"
          className="nerd-text-btn primary"
          disabled={!canSave}
        >
          Save
        </button>
        <button
          type="button"
          className="nerd-text-btn"
          onClick={() => {
            setEditing(false);
            setAdding(false);
          }}
        >
          Cancel
        </button>
      </div>
    </form>
  );

  const deckEditable = !disabled && !editLocked;

  const actions = (
    <div className="nerd-flash-actions">
      <button
        type="button"
        className="nerd-icon-btn"
        aria-label="Add card"
        disabled={!deckEditable || cards.length >= LIMITS.MAX_FLASHCARDS}
        onClick={startAdd}
        title={editLocked ? "Deck is locked" : undefined}
      >
        <Plus size={16} weight="bold" />
      </button>
      <button
        type="button"
        className="nerd-text-btn"
        disabled={!deckEditable || !card}
        onClick={startEdit}
      >
        Edit
      </button>
      <button
        type="button"
        className="nerd-icon-btn danger"
        aria-label="Delete card"
        disabled={!deckEditable || !card}
        onClick={() => card && onDelete(card.id)}
      >
        <Trash size={16} weight="bold" />
      </button>
    </div>
  );

  const batchButton = (
    <button
      type="button"
      className="nerd-icon-btn"
      aria-label="Batch import"
      disabled={!deckEditable || cards.length >= LIMITS.MAX_FLASHCARDS}
      onClick={openBatch}
      title={editLocked ? "Deck is locked" : "Batch import"}
    >
      <Cards size={16} weight="bold" />
    </button>
  );

  const lockButton = onToggleLock ? (
    <button
      type="button"
      className={`nerd-icon-btn${editLocked ? " pressed" : ""}`}
      aria-label={editLocked ? "Unlock editing" : "Lock editing"}
      aria-pressed={editLocked}
      disabled={disabled}
      onClick={() => onToggleLock(!editLocked)}
      title={editLocked ? "Deck locked" : "Lock deck editing"}
    >
      {editLocked ? (
        <Lock size={16} weight="bold" />
      ) : (
        <LockOpen size={16} weight="bold" />
      )}
    </button>
  ) : null;

  const deckTools = (
    <div className="nerd-deck-tools">
      {batchButton}
      {lockButton}
    </div>
  );

  return (
    <section
      className={`nerd-flash${fullBleed ? " full" : ""}${busy ? " busy" : ""}`}
      aria-label="Flashcards"
    >
      {followingName && onUnfollow && (
        <div className="nerd-follow-pill">
          <span>Following {followingName}</span>
          <button type="button" className="nerd-text-btn" onClick={onUnfollow}>
            Unfollow
          </button>
        </div>
      )}
      <div
        role="button"
        tabIndex={
          disabled || busy || viewLocked || unfocusable || (!card && !deckEditable)
            ? -1
            : 0
        }
        className={`nerd-flash-face${showGuide ? " is-empty" : ""}`}
        aria-label={
          showGuide
            ? "Add a flashcard"
            : flipped
              ? "Show term"
              : "Show definition"
        }
        aria-disabled={disabled || busy || viewLocked || (!card && !deckEditable)}
        onClick={(e) => {
          if (disabled || busy || viewLocked) return;
          if (!card) {
            if (deckEditable) startAdd();
            return;
          }
          onFlip();
          (e.currentTarget as HTMLElement).blur();
        }}
        onKeyDown={(e) => {
          if (disabled || busy || viewLocked) return;
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            if (!card) {
              if (deckEditable) startAdd();
              return;
            }
            onFlip();
            (e.currentTarget as HTMLElement).blur();
          }
        }}
      >
        {showGuide ? (
          <span className="nerd-flash-guide">
            <span className="nerd-flash-guide-title">Add a flashcard</span>
            <span className="nerd-flash-guide-hint">
              Click to write a term. Use + for one card, or the cards button
              to paste a list.
            </span>
          </span>
        ) : (
        <span
          ref={faceTextRef}
          className={`nerd-flash-text${card && !flipped ? " is-term" : ""}`}
        >
          {faceText}
        </span>
        )}
      </div>

      {(editing || adding) && (
        <div className="nerd-flash-form-dock">{editForm}</div>
      )}

      {fullBleed ? (
        <>
          <button
            type="button"
            className="nerd-side-arrow prev"
            aria-label="Previous card"
            disabled={disabled || cards.length < 2 || viewLocked}
            onClick={() => onNav(-1)}
          >
            <CaretLeft size={28} weight="thin" />
          </button>
          <button
            type="button"
            className="nerd-side-arrow next"
            aria-label="Next card"
            disabled={disabled || cards.length < 2 || viewLocked}
            onClick={() => onNav(1)}
          >
            <CaretRight size={28} weight="thin" />
          </button>

          <div className="nerd-corner nerd-corner-tl">
            <span className="nerd-flash-count">{countLabel}</span>
          </div>

          <div className="nerd-corner nerd-corner-tr">{deckTools}</div>

          <div className="nerd-corner nerd-corner-br">{actions}</div>
        </>
      ) : (
        <>
          {deckTools}
          <div className="nerd-flash-chrome">
            <div className="nerd-flash-toolbar">
              <div className="nerd-flash-nav">
                <button
                  type="button"
                  className="nerd-nav-char"
                  aria-label="Previous card"
                  disabled={disabled || cards.length < 2 || viewLocked}
                  onClick={() => onNav(-1)}
                >
                  <CaretLeft size={18} weight="thin" />
                </button>
                <span className="nerd-flash-count">{countLabel}</span>
                <button
                  type="button"
                  className="nerd-nav-char"
                  aria-label="Next card"
                  disabled={disabled || cards.length < 2 || viewLocked}
                  onClick={() => onNav(1)}
                >
                  <CaretRight size={18} weight="thin" />
                </button>
              </div>

              {actions}
            </div>
          </div>
        </>
      )}

      {batchOpen && (
        <div
          className="nerd-batch"
          role="dialog"
          aria-label="Batch import flashcards"
        >
          <div className="nerd-batch-head">
            <h2>Batch import</h2>
            <button
              type="button"
              className="nerd-icon-btn"
              aria-label="Close"
              onClick={() => setBatchOpen(false)}
            >
              <X size={16} weight="bold" />
            </button>
          </div>
          <p className="nerd-batch-lead">
            Paste cards as <code>Term :: Definition</code> — one per line.
          </p>
          <form className="nerd-batch-form" onSubmit={handleImport}>
            <div className="nerd-batch-scroll">
              <textarea
                value={batchText}
                onChange={(e) => {
                  setBatchText(e.target.value);
                  setImportError("");
                }}
                placeholder={
                  "Psychology :: study of mind and behavior\nNeuron :: a nerve cell"
                }
                autoFocus
              />
            </div>
            {importError && (
              <p className="nerd-batch-error">{importError}</p>
            )}
            <div className="nerd-batch-actions">
              <div className="nerd-batch-copy-wrap">
                <button
                  type="button"
                  className="nerd-icon-btn"
                  aria-label={
                    promptCopied ? "AI prompt copied" : "Copy AI prompt"
                  }
                  title="Copy AI prompt"
                  onClick={() => void copyAiPrompt()}
                >
                  <ClipboardText size={16} weight="bold" />
                </button>
                {promptCopied && (
                  <span className="nerd-copy-toast" role="status" aria-live="polite">
                    Prompt copied
                  </span>
                )}
              </div>
              <button
                type="submit"
                className="nerd-text-btn primary"
                disabled={disabled}
              >
                Import
              </button>
            </div>
          </form>
        </div>
      )}
    </section>
  );
}
